import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Shield, Zap, Leaf, Settings, LogOut, User,
  ChevronDown, Menu, X, AlertTriangle, HelpCircle
} from 'lucide-react';
import { useModuleAccess } from '../hooks/useModuleAccess';
import NotificationBell from './NotificationBell';

const TabNavigation = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);

  // Module access control
  const { isModuleEnabled, loading } = useModuleAccess();

  const allTabs = [
    { id: 'safety', label: 'Safety', icon: Shield, path: '/safety', moduleSlug: 'safety' },
    { id: 'energy', label: 'Energy/Resources', icon: Zap, path: '/energy-resources', moduleSlug: 'energy' },
    { id: 'sustainability', label: 'Sustainability', icon: Leaf, path: '/sustainability', moduleSlug: 'sustainability' },
    { id: 'boardpack', label: 'Board Governance', icon: AlertTriangle, path: '/board-pack', moduleSlug: 'board' },
    { id: 'admin', label: 'Admin', icon: Settings, path: '/admin', moduleSlug: 'admin', alwaysVisible: true } // Admin is core
  ];

  // Filter tabs based on enabled modules
  const tabs = allTabs.filter(tab =>
    tab.alwaysVisible || isModuleEnabled(tab.moduleSlug)
  );

  const getActiveTab = () => {
    const path = location.pathname;
    if (path.includes('/safety') || path.includes('/hira') || path.includes('/ptw') || 
        path.includes('/capa') || path.includes('/audits') || path.includes('/training') || 
        path.includes('/meetings') || path.includes('/ssem') || path.includes('/documents')) {
      return 'safety';
    }
    if (path.includes('/energy') || path.includes('/resources') || path.includes('/ers')) return 'energy';
    if (path.includes('/sustainability')) return 'sustainability';
    if (path.includes('/board-pack') || path.includes('/risk')) return 'boardpack';
    if (path.includes('/admin')) return 'admin';
    return 'dashboard';
  };

  const activeTab = getActiveTab();

  const handleTabClick = (tab) => {
    navigate(tab.path);
    setMobileMenuOpen(false);
  };

  return (
    <div className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-50">
      <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 gap-4">
          {/* Logo and Brand */}
          <div className="flex items-center flex-shrink-0 min-w-fit">
            <button
              onClick={() => navigate('/')}
              className="flex items-center space-x-3 hover:opacity-80 transition-opacity"
            >
              <img
                src="/saarv-logo.svg"
                alt="Saarv Logo"
                className="h-12 w-auto"
              />
              <div className="hidden lg:block border-l-2 border-gray-300 pl-3">
                <div className="text-sm font-bold text-gray-900 whitespace-nowrap">SAARV PLATFORM</div>
                <div className="text-xs text-gray-600 whitespace-nowrap">{user?.company_name || 'Company'}</div>
              </div>
            </button>
          </div>

          {/* Desktop Navigation Tabs */}
          <div className="hidden md:flex items-center space-x-1 flex-1 justify-center">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              
              return (
                <button
                  key={tab.id}
                  onClick={() => handleTabClick(tab)}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-semibold transition-colors ${
                    isActive
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </div>

          {/* Right Side Actions */}
          <div className="flex items-center space-x-3 flex-shrink-0">
            {/* Help Button */}
            <button
              onClick={() => window.open('/help', '_blank', 'noopener,noreferrer')}
              className="relative p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
              title="Help & Documentation"
            >
              <HelpCircle className="w-5 h-5" />
            </button>

            {/* Notifications */}
            <NotificationBell />

            {/* Profile Dropdown */}
            <div className="relative">
              <button
                onClick={() => setProfileDropdownOpen(!profileDropdownOpen)}
                className="flex items-center space-x-2 px-3 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                  <User className="w-5 h-5 text-white" />
                </div>
                <div className="hidden sm:block text-left">
                  <div className="text-sm font-semibold">{user?.name}</div>
                  <div className="text-xs text-gray-600">{user?.role}</div>
                </div>
                <ChevronDown className="w-4 h-4" />
              </button>

              {/* Dropdown Menu */}
              {profileDropdownOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                  <button
                    onClick={() => {
                      window.open('/help', '_blank', 'noopener,noreferrer');
                      setProfileDropdownOpen(false);
                    }}
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
                  >
                    <HelpCircle className="w-4 h-4" />
                    <span>Help & Documentation</span>
                  </button>
                  <button
                    onClick={() => {
                      navigate('/admin');
                      setProfileDropdownOpen(false);
                    }}
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
                  >
                    <Settings className="w-4 h-4" />
                    <span>Settings</span>
                  </button>
                  <hr className="my-1" />
                  <button
                    onClick={() => {
                      setProfileDropdownOpen(false);
                      onLogout();
                    }}
                    className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center space-x-2"
                  >
                    <LogOut className="w-4 h-4" />
                    <span>Logout</span>
                  </button>
                </div>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-gray-200">
            <div className="space-y-1">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;

                return (
                  <button
                    key={tab.id}
                    onClick={() => handleTabClick(tab)}
                    className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg font-semibold transition-colors ${
                      isActive
                        ? 'bg-blue-600 text-white'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    <span>{tab.label}</span>
                  </button>
                );
              })}

              {/* Help Link in Mobile Menu */}
              <button
                onClick={() => {
                  window.open('/help', '_blank', 'noopener,noreferrer');
                  setMobileMenuOpen(false);
                }}
                className="w-full flex items-center space-x-3 px-4 py-3 rounded-lg font-semibold text-gray-700 hover:bg-gray-100 transition-colors"
              >
                <HelpCircle className="w-5 h-5" />
                <span>Help & Documentation</span>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Close dropdown when clicking outside */}
      {profileDropdownOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setProfileDropdownOpen(false)}
        />
      )}
    </div>
  );
};

export default TabNavigation;
